/*
  <smx-soap-calc>: page code for sections/smx-soap-calc.liquid.
  Reads the chart and wording from the section's JSON, runs the math in
  smx-soap-calc-logic.js (window.SmxSoapCalc) on every input change and
  writes the result, the explanation and the install steps on the same page.
*/
if (!customElements.get('smx-soap-calc')) {
  class SmxSoapCalc extends HTMLElement {
    connectedCallback() {
      const start = () => {
        if (!window.SmxSoapCalc) return setTimeout(start, 50);
        this.init();
      };
      start();
    }

    init() {
      const raw = this.querySelector('[data-smx-soap-data]');
      try { this.data = JSON.parse(raw.textContent); } catch (e) { return; }
      this.t = this.data.labels || {};
      this.form = this.querySelector('[data-smx-soap-form]');
      this.out = this.querySelector('[data-smx-soap-results]');
      this.gpm = this.querySelector('[data-smx-soap-gpm]');
      this.chemOther = this.querySelector('[data-smx-soap-chem-other]');
      this.chemCustom = this.querySelector('[data-smx-soap-chem-custom]');
      this.targetOther = this.querySelector('[data-smx-soap-target-other]');
      this.targetCustom = this.querySelector('[data-smx-soap-target-custom]');
      this.steps = Array.from(this.querySelectorAll('[data-smx-soap-step]'));
      this.rows = Array.from(this.querySelectorAll('[data-smx-soap-row]'));

      // Optional prefill from the address, e.g. ?gpm=3.2 (other query and UTM parameters are left alone).
      const params = new URLSearchParams(window.location.search);
      if (params.get('gpm') && this.gpm) this.gpm.value = params.get('gpm');

      let timer;
      const update = () => { clearTimeout(timer); timer = setTimeout(() => this.render(), 120); };
      this.form.addEventListener('input', update);
      this.form.addEventListener('change', () => this.render());

      const useCustom = this.querySelector('[data-smx-soap-use-custom]');
      if (useCustom) useCustom.addEventListener('click', () => {
        const radio = this.form.querySelector('input[name="surface"][value="custom"]');
        if (radio) { radio.checked = true; this.render(); this.targetCustom.focus(); }
      });

      // Load videos only when their accordion opens.
      this.querySelectorAll('[data-smx-soap-video]').forEach((d) => d.addEventListener('toggle', () => {
        const f = d.querySelector('iframe[data-src]');
        if (d.open && f && !f.src) f.src = f.dataset.src;
      }));

      this.render();
    }

    fill(text, values) {
      return String(text || '').replace(/\[(\w+)\]/g, (m, k) => (k in values ? values[k] : m));
    }

    esc(text) {
      const d = document.createElement('div');
      d.textContent = text;
      return d.innerHTML;
    }

    readInputs() {
      const chem = this.form.querySelector('input[name="chemical"]:checked');
      const surface = this.form.querySelector('input[name="surface"]:checked');
      const chemIsOther = chem && chem.value === 'other';
      const surfaceIsCustom = surface && surface.value === 'custom';
      this.chemOther.hidden = !chemIsOther;
      this.targetOther.hidden = !surfaceIsCustom;
      return {
        gpm: this.gpm.value,
        chemical: chemIsOther ? this.chemCustom.value : chem ? chem.value : '',
        chemicalTyped: chemIsOther,
        target: surfaceIsCustom ? this.targetCustom.value : surface ? surface.value : '',
        surfaceLabel: surface && !surfaceIsCustom ? surface.dataset.label : '',
        surfacePicked: !!surface,
        surfaceIsCustom,
      };
    }

    render() {
      const C = window.SmxSoapCalc;
      const t = this.t;
      const f = C.format;
      const input = this.readInputs();
      const orifices = this.data.orifices.map((o) => ({ id: o.id, min: C.parseNumber(o.min), max: C.parseNumber(o.max) }));
      const range = { min: f(orifices[0].min), max: f(orifices[orifices.length - 1].max) };

      let result = null;
      let message = '';
      const gpmFilled = String(input.gpm).trim() !== '';
      if (!gpmFilled) {
        message = t.empty;
      } else {
        const gpm = C.parseNumber(input.gpm);
        const orifice = C.pickOrifice(gpm, orifices);
        if (!orifice) {
          message = this.fill(t.err_gpm_range, range);
          this.out.innerHTML = `<p class="smx-soap__error" role="alert">${this.esc(message)}</p>`;
          this.updateSteps(null);
          return;
        }
        if (!input.surfacePicked || (input.surfaceIsCustom && String(input.target).trim() === '')) {
          this.out.innerHTML = this.partsHtml({ orifice, pick: null }) + `<p class="smx-soap__empty">${this.esc(t.pick_surface)}</p>`;
          this.updateSteps({ orifice, pick: null });
          return;
        }
        result = C.recommend({
          gpm: input.gpm,
          chemical: input.chemical,
          target: input.target,
          orifices,
          proportioners: this.data.proportioners,
          tolerance: this.data.tolerance,
        });
      }

      if (!result) {
        this.out.innerHTML = `<p class="smx-soap__empty">${this.esc(message)}</p>`;
        this.updateSteps(null);
        return;
      }
      if (!result.ok) {
        const map = { 'gpm-missing': t.err_gpm_range, 'gpm-range': t.err_gpm_range, 'chemical-invalid': t.err_chemical, 'target-invalid': t.err_target, 'target-too-high': t.err_target_high };
        const text = this.fill(map[result.error] || t.err_target, range);
        this.out.innerHTML = (result.orifice ? this.partsHtml({ orifice: result.orifice, pick: null }) : '') + `<p class="smx-soap__error" role="alert">${this.esc(text)}</p>`;
        this.updateSteps(result.orifice ? { orifice: result.orifice, pick: null } : null);
        return;
      }

      const v = {
        gpm: f(result.gpm),
        orifice: result.orifice.id,
        surface: input.surfaceLabel.toLowerCase(),
        target: f(result.target),
        chemical: f(result.chemical),
        desired: f(result.desired),
        color: result.pick.name,
        ratio: f(result.pick.ratio),
        strength: f(result.pick.strength),
        backup_color: result.stronger ? result.stronger.name : '',
        backup_ratio: result.stronger ? f(result.stronger.ratio) : '',
        backup_strength: result.stronger ? f(result.stronger.strength) : '',
      };
      const match = { exact: t.ex_5_exact, close: t.ex_5_close, milder: t.ex_5_milder, 'mildest-available': t.ex_5_mildest }[result.match];
      const steps = [
        this.fill(t.ex_1, v),
        this.fill(input.surfaceIsCustom ? t.ex_2_custom : t.ex_2, v),
        this.fill(t.ex_3, v),
        `${this.esc(this.fill(t.ex_4, v))}<span class="smx-soap__formula">${v.chemical}% ÷ ${v.target}% = ${v.desired}:1</span>`,
        this.fill(match, v),
        `${this.esc(this.fill(t.ex_6, v))}<span class="smx-soap__formula">${v.chemical}% ÷ ${v.ratio} = ${v.strength}%</span>`,
      ];
      const advice = result.stronger
        ? `<p>${this.esc(this.fill(t.advice, v))}</p><p><strong>${this.esc(this.fill(t.advice_backup, v))}</strong></p>`
        : `<p>${this.esc(this.fill(t.advice_no_backup, v))}</p>`;

      this.out.innerHTML =
        this.partsHtml(result) +
        `<dl class="smx-soap__facts">
          <div><dt>${this.esc(t.lbl_ratio)}</dt><dd>${v.ratio}:1</dd></div>
          <div><dt>${this.esc(t.lbl_strength)}</dt><dd>${v.strength}%</dd></div>
          <div><dt>${this.esc(t.lbl_target)}</dt><dd>${this.esc(t.about)} ${v.target}%</dd></div>
        </dl>
        <div class="smx-soap__explain">
          <h3 class="smx-soap__h3">${this.esc(t.explain_heading)}</h3>
          <ol>${steps.map((s, i) => `<li>${i === 3 || i === 5 ? s : this.esc(s)}</li>`).join('')}</ol>
          <div class="smx-soap__advice">${advice}</div>
          <p class="smx-soap__rule">${this.esc(t.rule)}</p>
        </div>`;
      this.updateSteps(result);
    }

    // The two big "parts" cards: orifice and proportioner.
    partsHtml(r) {
      const t = this.t;
      const prop = r.pick
        ? `<div class="smx-soap__part">
            <span class="smx-soap__part-label">${this.esc(t.lbl_proportioner)}</span>
            <span class="smx-soap__prop" style="--swatch: ${this.esc(r.pick.color || 'transparent')}" aria-hidden="true"></span>
            <span class="smx-soap__part-value">${this.esc(r.pick.name)}</span>
          </div>`
        : '';
      return `<div class="smx-soap__parts">
          <div class="smx-soap__part">
            <span class="smx-soap__part-label">${this.esc(t.lbl_orifice)}</span>
            <img class="smx-soap__orifice-img" src="${this.esc(this.data.orificeImage)}" alt="" width="120" height="140">
            <span class="smx-soap__part-value">#${this.esc(r.orifice.id)}</span>
          </div>${prop}
        </div>`;
    }

    updateSteps(r) {
      const t = this.t;
      const values = {
        orifice: r && r.orifice ? `#${r.orifice.id}` : t.orifice_unknown,
        color: r && r.pick ? r.pick.name : t.color_unknown,
      };
      // The orifice number and color are bold so they stand out in the steps.
      const bold = { orifice: `<strong>${this.esc(values.orifice)}</strong>`, color: `<strong>${this.esc(values.color)}</strong>` };
      this.steps.forEach((el) => { el.innerHTML = this.fill(this.esc(el.dataset.smxSoapStep), bold); });
      // Highlight the customer's column and row in the full chart.
      const col = r && r.orifice ? r.orifice.id : null;
      this.querySelectorAll('.smx-soap__table td').forEach((td) => td.classList.toggle('is-col', td.dataset.col === col));
      this.rows.forEach((tr) => tr.classList.toggle('is-pick', !!(r && r.pick && tr.dataset.smxSoapRow === r.pick.name)));
    }
  }
  customElements.define('smx-soap-calc', SmxSoapCalc);
}
